import React, {useState, useContext, useEffect} from "react";
import { useIntl, FormattedMessage } from "react-intl";
import AuthenticationService from "../../../services/AuthenticationService";
import ErrorBoundary from "../../ErrorBoundary";
import AddUserDialog from "./AddUserDialog";
import UsersTable from "./UsersTable";
import { UserPlusIcon } from "@heroicons/react/24/solid";
import { PageHeading } from "../../Container";
import Button from "../../Button";
import CountryContext from "../../../CountryContext";
import {useUsers} from "@feature/users/hooks/useUsers";
import {useRoles} from "@feature/roles/hooks/useRoles";

function Users() {

  // context
  const { country: {code: countryCode} } = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const {fetch, fetchInternals, add, update, remove, loading} = useUsers(countryCode);
  const {data: roles} = useRoles();

  // state
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [refreshObservable, setRefreshObservable] = useState(false);
  const [internals, setInternals] = useState();

  // utilities
  const scopes = AuthenticationService.getUserScopes();
  const canCreateUser =
    scopes.indexOf("user:create") >= 0
    && !!roles;
  const refreshTable = () => setRefreshObservable((prev) => !prev);

  useEffect(() => {
    // load internal users on load
    fetchInternals().then(setInternals);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return <>
    <PageHeading
      title={intl.formatMessage({ id: "users" })}
      contentActions={<>
        {!!canCreateUser && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => { setShowNewUserDialog(true); }}
          >
            <UserPlusIcon
              className="-ml-0.5 mr-2 h-4 w-4"
              aria-hidden="true"
            />
            <FormattedMessage id="add" />
          </Button>
        )}
      </>}
    />
    {!!internals && !!roles && (
      <ErrorBoundary from="users-table">
        <UsersTable
          {...{fetch, update, remove, loading}}
          refreshObservable={refreshObservable}
          roles={roles}
          internalUsers={internals}
        />
      </ErrorBoundary>
    )}
    {showNewUserDialog && (
      <ErrorBoundary from="users-add-dialog">
        <AddUserDialog
          roles={roles}
          onConfirm={async (data) => {
            const result = await add(data);
            if (!!result) {
              refreshTable();
              setShowNewUserDialog(false);
            }
          }}
          onClose={() => setShowNewUserDialog(false)}
          loading={loading}
        />
      </ErrorBoundary>
    )}
  </>
}

export default Users;
